import { AfterViewInit, Component, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { FilesProvider } from '../../../files/providers/files-provider';
import { GuestRights } from '../../providers/guests-rights';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { GuestsProvider, GuestRow } from '../../providers/guests-provider';
import {
  MAT_DIALOG_DATA, MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Organization } from "../../../common/models/organization";
import { User } from "../../../common/models/user";
import { Lang } from "../../../common/models/lang";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { Guest } from "../../../common/models/guest";
import { map, switchMap } from "rxjs/operators";
import { Observable } from "rxjs";
import { NgIf } from "@angular/common";
import { InputImageComponent } from "../../../common/components/input-image/input-image";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatError, MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";

@Component({
  selector: 'guest-form',
  providers: [],
  styleUrls: ['./form.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogClose,
    NgIf,
    MatDialogTitle,
    InputImageComponent,
    MatIconButton,
    MatButton,
    MatFormField,
    MatInput,
    MatProgressBar,
    MatTooltip,
    TranslateModule,
    MatDialogContent,
    MatError,
    MatDialogActions,
    MatIcon
  ],
  templateUrl: './form.html'
})
export class GuestFormComponent implements AfterViewInit {
    public readonly NAME_MAX_LENGTH: number = 200;
    public readonly CHECK_EMAIL_THRESHOLD: number = 750;
    public isLoading = false;
    public formTitle = '';

    public organization: Organization | null = null;
    public guestId = 0;
    public user: User = new User({});

    public langId: number = Lang.ID_DEFAULT;

    public avatarImage: any = { current: null, new: null };

    public firstnameFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public lastnameFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public emailUnavailableError = false;
    public emailFormControl: FormControl = new FormControl('', [
        Validators.required,
        Validators.email,
        (control: any) => this.emailUnavailableError ? { unavailable: { valid: false } } : null
    ]);
    public phoneNumberFormControl: FormControl = new FormControl('');
    public rights: GuestRights = new GuestRights(this.utils);

    private _checkEmailAvailabilityRequestTimestamp = 0;
    private _originalEmail: string | null = null;
    constructor(
        public conf: ConfigProvider,
        public currentUser: CurrentUser,
        public utils: UtilsProvider,
        public dialog: MatDialogRef<GuestFormComponent>,
        public usersProvider: UsersProvider,
        private _toaster: MatSnackBar,
        private _title: Title,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _router: Router,
        private _route: ActivatedRoute,
        private _langsProvider: LangsProvider,
        private _translater: TranslateService,
        private _files: FilesProvider,
        private _guestsProvider: GuestsProvider,
        @Inject(MAT_DIALOG_DATA) private _data: any
    ) {
        this.organization = _data.organization;
        this.formTitle = this._data.formTitle;

        if (!this.organization?.id) return;
        this.rights.evaluateScreenRights({
            rolesIds: this._currentUserRoles.getRolesIds(this.organization.id)
        });

        if (this._data.guestRow) {

            this.guestId = this._data.guestRow.guest.id;
            this.user = this._data.guestRow.user;
            this.langId = this._data.guestRow.user.langId;

            this.firstnameFormControl.setValue(this._data.guestRow.user.firstName);
            this.lastnameFormControl.setValue(this._data.guestRow.user.lastName);
            this.emailFormControl.setValue(this._data.guestRow.user.email);
            this._originalEmail = this._data.guestRow.user.email;
            this.phoneNumberFormControl.setValue(this._data.guestRow.user.phoneNumber);

            this.avatarImage.current = _data.guestRow.user.avatar;

        }
    }

    public ngAfterViewInit() {
    }

    public onSaveClicked(): void {
        if (this.canSave()) {
            this.isLoading = true;
            this.save().subscribe({
                next: (result: GuestRow) => {
                    this._toaster.open(this._translater.instant('Guest_saved'), '', { duration: 5000 });
                    this.isLoading = false;
                    this.dialog.close(result);
                },
                error: (error: any) => this.onError(error)
            });
        } else {
            this.touchFields();
            this._toaster.open(this._translater.instant('Correct_form_errors'), '', { duration: 5000 });
        }
    }

    public canSave(): boolean {
        return !this.isLoading
            && !this.firstnameFormControl.invalid
            && !this.lastnameFormControl.invalid
            && !this.emailFormControl.invalid
            && !this.phoneNumberFormControl.invalid;
    }

    public onEmailChanged(): void {
        this.emailUnavailableError = false;
        this.emailFormControl.markAsTouched();
        this.emailFormControl.updateValueAndValidity();
        if (!this.emailFormControl.hasError('email') && this.emailFormControl.value != this._originalEmail) {
            // on check la dispo que si l'email est valide et différente de l'originale ...
            this.checkEmailAvailability();
        }
    }

    private touchFields(): void {
        this.firstnameFormControl.markAsTouched();
        this.lastnameFormControl.markAsTouched();
        this.phoneNumberFormControl.markAsTouched();
        this.emailFormControl.markAsTouched();
    }

    private checkEmailAvailability(): void {
        this._checkEmailAvailabilityRequestTimestamp = (new Date()).getTime();
        setTimeout(() => {
            const now: number = (new Date()).getTime();
            if (now - this._checkEmailAvailabilityRequestTimestamp >= this.CHECK_EMAIL_THRESHOLD) {
                this.usersProvider.checkEmailAvailability({email: this.emailFormControl.value, deep: false}).subscribe({
                    next: (isAvailable: boolean) => {
                        this.emailUnavailableError = !isAvailable;
                        this.emailFormControl.updateValueAndValidity();
                    },
                    error: (error: any) => this.onError(error)
                });
            }
        }, this.CHECK_EMAIL_THRESHOLD);
    }

    private save(): Observable<GuestRow> {
        const guest = new Guest({});
        guest.id = this.guestId;
        guest.userId = this.user.id;
        const user = this.user;
        user.firstName = this.firstnameFormControl.value;
        user.lastName = this.lastnameFormControl.value;
        user.email = this.emailFormControl.value;
        user.phoneNumber = this.phoneNumberFormControl.value;
        user.isAdmin = 0;
        user.langId = this.currentUser.user!.langId;
        if (this.avatarImage.new) {
            user.avatar = this.avatarImage.new;
        } else if (!this.avatarImage.current) {
            user.avatar = undefined;
        }
        if (!this.organization?.id) throw new Error('No organization id');
        return this._guestsProvider.saveGuest(this.organization.id, guest, user).pipe(
          switchMap((guest: Guest) => {
            return this._guestsProvider.getGuestRow(this.organization!.id!, guest.userId!);
          }),
        );
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 422) {
            if (error.error.errors && error.error.errors.email) {
                // c'est qu'on a essayé de faire valider un email test@test par exemple (c'est validé par le validator angular mais pas laravel...)
                return;
            }
        }

        this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
    }

}
